/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch } from 'react-redux';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import type { serviceTypes } from '@opengeoweb/store';
import { mapSelectors, serviceActions } from '@opengeoweb/store';
import { clearImageCacheForAllMaps } from '@opengeoweb/webmap-react';
import type { ServicePopupInfo } from './ServiceOptionsDialog';
import { ServiceOptionsDialog } from './ServiceOptionsDialog';
import type { layerSelectTypes } from '../../store';
import {
  layerSelectActions,
  layerSelectSelectors,
  useAppSelector,
} from '../../store';
import { useLayerSelectTranslation } from '../../utils/i18n';
import { loadWMSService } from '../ServicePopup/utils';

interface ServiceOptionsDialogConnectProps {
  mapId?: string;
}

const getService = (
  services: layerSelectTypes.ActiveServiceObjectEntities,
  serviceUrl: string,
): layerSelectTypes.ActiveServiceObject | null => {
  const serviceIds = Object.keys(services);
  const serviceIndex = serviceIds.findIndex(
    (serviceId) => services[serviceId]?.serviceUrl === serviceUrl,
  );
  if (serviceIndex < 0) {
    return null;
  }
  return services[serviceIds[serviceIndex]];
};

export const getServiceSuccesUpdateMessage = (
  serviceName: string,
): snackbarTypes.TranslatableMessage => ({
  type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
  key: 'service-successfully-updated',
  params: {
    serviceName,
  },
});

export const ServiceOptionsDialogConnect: React.FC<
  ServiceOptionsDialogConnectProps
> = ({ mapId }) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);

  const dispatch = useDispatch();
  const { t } = useLayerSelectTranslation();

  const serviceSetLayers = React.useCallback(
    (payload: serviceTypes.SetLayersForServicePayload): void => {
      dispatch(
        serviceActions.serviceSetLayers({ ...payload, isUpdating: true }),
      );
    },
    [dispatch],
  );

  const setServicePopupInfo = React.useCallback(
    (popupInfo: ServicePopupInfo): void => {
      const payload: layerSelectTypes.ToggleServicePopupPayload = {
        ...popupInfo,
        variant: popupInfo.variant! || 'default',
      };
      dispatch(layerSelectActions.toggleServicePopup(payload));
    },
    [dispatch],
  );

  const showSnackbar = React.useCallback(
    (message: snackbarTypes.TranslatableMessage) => {
      dispatch(snackbarActions.openSnackbar(message));
    },
    [dispatch],
  );

  const services = useAppSelector((store) =>
    layerSelectSelectors.getActiveServices(store),
  );

  const selectedLayers = useAppSelector((store) =>
    mapSelectors.getMapLayersWithoutDimensionCurrentValue(store, mapId!),
  );

  const layerSelectRemoveService = React.useCallback(
    (serviceId: string, serviceUrl: string, serviceName: string) => {
      const message: snackbarTypes.TranslatableMessage = {
        type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
        key: 'service-has-been-deleted',
        params: {
          serviceName,
        },
      };
      showSnackbar(message);
      dispatch(
        layerSelectActions.layerSelectRemoveService({
          serviceId,
          serviceUrl,
        }),
      );
    },
    [dispatch, showSnackbar],
  );

  const layerSelectReloadService = React.useCallback(
    async (serviceUrl: string) => {
      setIsLoading(true);
      setError(undefined);
      try {
        const layersForService = await loadWMSService(serviceUrl, true);

        const service = getService(services, serviceUrl);

        serviceSetLayers({
          id: layersForService.id,
          name: service?.serviceName || layersForService.name,
          serviceUrl,
          abstract: service?.abstract || layersForService.abstract,
          layers: layersForService.layers,
          scope: service?.scope || 'user',
          isUpdating: true,
        });
        clearImageCacheForAllMaps();
        const serviceTitle = service?.serviceName || layersForService.name;
        showSnackbar(getServiceSuccesUpdateMessage(serviceTitle));
      } catch (error) {
        setError(
          t('unable-to-update-service', { message: (error as Error).message }),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [serviceSetLayers, services, showSnackbar, t],
  );

  return (
    <ServiceOptionsDialog
      error={error}
      isLoading={isLoading}
      layerSelectReloadService={layerSelectReloadService}
      layerSelectRemoveService={layerSelectRemoveService}
      selectedLayers={selectedLayers}
      services={services}
      setServicePopupInfo={setServicePopupInfo}
    />
  );
};
